import { create } from "zustand";
import {
  parseApiError,
  getContextualErrorMessage,
  formatErrorForLogging,
} from "@/utils/responseParser";

interface NotificationData {
  status: "success" | "error" | "warning" | "info";
  message: string;
  source?: string | null;
  exceptionId?: string;
  duration?: number;
  timestamp: number;
  id: string;
}

interface NotificationState {
  // Legacy properties - maintain for backward compatibility
  status: "success" | "error" | null;
  message: string | null;
  selectedRowId: string | number | null;
  menuVisible: boolean;
  showSuccessIcon: boolean;
  source: string | null;

  // Enhanced notification system
  notifications: NotificationData[];

  // Legacy methods - maintain for backward compatibility
  setNotification: (
    status: "success" | "error",
    message: string,
    source?: string | null
  ) => void;
  clearNotification: () => void;
  setSuccessIconVisibility: (visible: boolean) => void;
  toggleMenuVisible: (isVisible: boolean) => void;
  setSelectedRowId: (id: string | number | null) => void;

  showUnsavedChangesNotification: boolean;
  setShowUnsavedChangesNotification: (value: boolean) => void;

  // Enhanced methods
  addApiErrorNotification: (
    error: any,
    context?: string,
    source?: string
  ) => void;
  addSuccessNotification: (message: string, source?: string) => void;
  addWarningNotification: (message: string, source?: string) => void;
  addInfoNotification: (message: string, source?: string) => void;
  removeNotification: (id: string) => void;
  clearAllNotifications: () => void;
}

const generateId = () =>
  `notification_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;

export const useNotificationStore = create<NotificationState>((set) => ({
  // Legacy properties
  status: null,
  message: null,
  showSuccessIcon: false,
  menuVisible: false,
  showUnsavedChangesNotification: false,
  setShowUnsavedChangesNotification: (value) =>
    set({ showUnsavedChangesNotification: value }),
  source: null,
  selectedRowId: null,

  // Enhanced properties
  notifications: [],

  // Legacy methods - maintain backward compatibility
  setNotification: (status, message, source = null) => {
    set({ status, message, source });

    // Also add to enhanced notifications for consistency
    const notification: NotificationData = {
      id: generateId(),
      status,
      message,
      source,
      timestamp: Date.now(),
      duration: status === "error" ? 10000 : 5000,
    };

    set((state) => ({
      notifications: [...state.notifications, notification],
    }));
  },

  clearNotification: () => set({ status: null, message: null, source: null }),
  setSuccessIconVisibility: (visible) => set({ showSuccessIcon: visible }),
  toggleMenuVisible: (isVisible) => set({ menuVisible: isVisible }),
  setSelectedRowId: (id) => set({ selectedRowId: id }),

  // Enhanced methods with backend message parsing
  addApiErrorNotification: (error: any, context?: string, source?: string) => {
    const parsedError = parseApiError(error);
    const contextualMessage = context
      ? getContextualErrorMessage(error, context as any)
      : parsedError.message;

    // Log error for debugging
    if (process.env.NODE_ENV === "development") {
      console.error("API Error:", formatErrorForLogging(error, context));
    }

    const notification: NotificationData = {
      id: generateId(),
      status: "error",
      message: contextualMessage,
      source,
      exceptionId: parsedError.exceptionId,
      timestamp: Date.now(),
      duration: 10000, // Longer duration for errors
    };

    set((state) => ({
      notifications: [...state.notifications, notification],
      // Update legacy properties for backward compatibility
      status: "error",
      message: contextualMessage,
      source,
    }));
  },

  addSuccessNotification: (message: string, source?: string) => {
    const notification: NotificationData = {
      id: generateId(),
      status: "success",
      message,
      source,
      timestamp: Date.now(),
      duration: 5000,
    };

    set((state) => ({
      notifications: [...state.notifications, notification],
      status: "success",
      message,
      source,
    }));
  },

  addWarningNotification: (message: string, source?: string) => {
    const notification: NotificationData = {
      id: generateId(),
      status: "warning",
      message,
      source,
      timestamp: Date.now(),
      duration: 6000,
    };

    set((state) => ({
      notifications: [...state.notifications, notification],
    }));
  },

  addInfoNotification: (message: string, source?: string) => {
    const notification: NotificationData = {
      id: generateId(),
      status: "info",
      message,
      source,
      timestamp: Date.now(),
      duration: 5000,
    };

    set((state) => ({
      notifications: [...state.notifications, notification],
    }));
  },

  removeNotification: (id: string) => {
    set((state) => ({
      notifications: state.notifications.filter((n) => n.id !== id),
    }));
  },

  clearAllNotifications: () => {
    set({
      notifications: [],
      status: null,
      message: null,
      source: null,
    });
  },
}));
